import ElectricalServicesIcon from "@mui/icons-material/ElectricalServices"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Step,
    StepLabel,
    Stepper,
    useTheme,
} from "@mui/material"
import _ from "lodash"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import * as tweenly from "tweenly"
import * as yup from "yup"
import { isValidUrlOrProxy, wrapUrlForProxy } from "../../../helpers/url.helpers"
import { useEditorSettings } from "../../../hooks/useEditorSettings"
import useFramerate from "../../../hooks/useFramerate"
import { useAppDispatch } from "../../../hooks/useRedux"
import { useReduxForm } from "../../../hooks/useReduxForm"
import useValidationDataSource from "../../../hooks/useValidationDataSource"
import { AssetT, createAssetAsync, updateAssetAsync } from "../../../store/slices/assets.slice"
import { Transition } from "../../common/Transition"
import FirstStep from "./FirstStep"
import SecondStep from "./SecondStep"
import ThirdStep from "./ThirdStep"
import "./yupExtensions" // Import the yup extensions
import { confirmOnEnter } from "../../../helpers/dialog.helpers"

interface Props {
    selectedAsset?: AssetT
    open: boolean
    onClose: () => void
}

export type AttributeT = {
    name?: string
    path?: string
}

type FormData = AssetT & {
    refresh: boolean
    refreshInterval: number
    JSONdata?: string
    rawJSON?: string
    proxy: boolean
}

const DEFAULT_VALUES = {
    name: "",
    slug: "",
    url: "",
    type: "json",
    refresh: false,
    refreshInterval: 1,
    JSONdata: "",
    rawJSON: "",
    attributes: [],
    proxy: false,
}

const DataSourceDialog = ({ selectedAsset, open, onClose }: Props) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { debounceVerify } = useValidationDataSource()

    // Define the types for the validation schema
    const validationSchema = [
        yup.object({
            name: yup.string().required(),
            slug: yup
                .string()
                .required()
                .test(
                    "custom-slug-validation",
                    t("dataSource:formErrorMsg.notUniqueSlug") as string,
                    (value) =>
                        new Promise((resolve) => debounceVerify(selectedAsset, value, resolve))
                ),
            url: yup
                .string()
                .required()
                .test("url-or-proxy", t("dataSource:formErrorMsg.invalidUrl") as string, (value) =>
                    isValidUrlOrProxy(value)
                ),
            type: yup.string().required(),
            refresh: yup.boolean(),
            refreshInterval: yup
                .number()
                .transform((curr, orig) => (orig === "" ? 0 : curr))
                .when("refresh", {
                    is: true,
                    then: yup
                        .number()
                        .min(0.001, "dataSource:refreshIntervalValidationMin")
                        .required("dataSource:refreshIntervalValidationRequired"),
                }),
        }),
        yup.object({
            JSONdata: yup.string().required(),
        }),
        yup.object({
            attributes: yup
                .array()
                .of(
                    yup.object().shape({
                        path: yup.string().required(),
                        name: yup.string().required(),
                    })
                )
                .unique(t("dataSource:formErrorMsg.uniqueAttributeName"), (a: any) => a.name),
        }),
    ]
    const steps = [
        t("dataSource:formSteps.step0"),
        t("dataSource:formSteps.step1"),
        t("dataSource:formSteps.step2"),
    ]
    const [activeStep, setActiveStep] = useState<number>(0)
    const [loading, setLoading] = useState<boolean>(false)
    const currentValidationSchema = validationSchema[activeStep]
    const [error, setError] = useState<boolean>(false)
    const { setFramerateValue, resultTimeFramerate } = useFramerate()

    const dataSourceData = useMemo(() => {
        // Convert refreshInterval (ms) back to display units (seconds or frames)
        // Default is 1 second = 1 sec | 25 frames at 25fps | 50 frames at 50fps
        let refreshInterval = resultTimeFramerate(1)
        if (selectedAsset?.refreshInterval) {
            // refreshInterval is in ms, convert to seconds first, then to display units
            const refreshInSeconds = selectedAsset.refreshInterval / 1000
            refreshInterval = resultTimeFramerate(refreshInSeconds)
        }
        return { ...DEFAULT_VALUES, ...selectedAsset, refreshInterval }
    }, [selectedAsset, resultTimeFramerate])

    const disableFormInput = selectedAsset && selectedAsset._id ? true : false

    const handleNext = async () => {
        const isStepValid = await trigger()
        if (isStepValid && url && activeStep === 0) {
            setJSONData()
        }
        if (isStepValid) setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
    const handleBack = () => {
        if (activeStep === 1) {
            setValue("proxy", false)
        }
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }
    const handleCancel = () => {
        setActiveStep(0)
        reset(DEFAULT_VALUES)
        onClose()
    }

    const { companyId } = useEditorSettings()

    const onSubmitDataSource = async (data: FormData) => {
        // Convert refreshTime (in display units - seconds or frames) to milliseconds
        // setFramerateValue converts from frames to seconds, then we convert to ms
        const refreshIntervalMs = data.refresh
            ? Math.round(setFramerateValue(data.refreshInterval) * 1000)
            : undefined

        if (selectedAsset && selectedAsset._id) {
            dispatch(
                updateAssetAsync(selectedAsset._id, {
                    ...selectedAsset,
                    attributes: data.attributes,
                    name: data.name,
                    refresh: data.refresh,
                    refreshInterval: refreshIntervalMs,
                })
            )
        } else {
            const asset = {
                ...data,
                url: data.url && (data.proxy ? wrapUrlForProxy(data.url) : data.url),
                rawJSON: undefined,
                JSONdata: undefined,
                refresh: data.refresh,
                refreshInterval: refreshIntervalMs,
            }
            dispatch(createAssetAsync(asset, companyId))
        }

        setActiveStep(0)
        reset(DEFAULT_VALUES)
        onClose()
    }

    const {
        control,
        setValue,
        handleSubmit,
        trigger,
        reset,
        formState: { errors, isSubmitting, isValid },
    } = useReduxForm({
        schema: currentValidationSchema,
        defaultValues: dataSourceData,
    })
    const type = useWatch({
        control,
        name: "type",
    })
    const url = useWatch({
        control,
        name: "url",
    })

    const rawJSON = useWatch({
        control,
        name: "rawJSON",
    })

    const proxy = useWatch({
        control,
        name: "proxy",
    })

    const setJSONData = useCallback(async () => {
        setLoading(true)
        const finalUrl = proxy ? wrapUrlForProxy(url) : url
        const res = await tweenly.fetchAsset(finalUrl, type)
        if (!res) {
            return
        }
        const { data } = res

        setLoading(false)

        if (data === undefined || _.isEmpty(data)) {
            setError(true)
            setValue("JSONdata", "")
            setValue("rawJSON", "")
        } else {
            setError(false)
            setValue("JSONdata", JSON.stringify(data, undefined, 2))
            setValue("rawJSON", data)
        }
    }, [proxy, setValue, type, url])

    useEffect(() => {
        if (!proxy || !url) return
        setJSONData()
    }, [proxy, url, setJSONData])

    function getStepContent(step: number) {
        switch (step) {
            case 0:
                return (
                    <FirstStep
                        control={control}
                        errors={errors}
                        disableFormInput={disableFormInput}
                    />
                )
            case 1:
                return (
                    <SecondStep
                        control={control}
                        loading={loading}
                        error={error}
                        errors={errors}
                        type={type}
                    />
                )
            case 2:
                return <ThirdStep control={control} errors={errors} rawJSON={rawJSON} />
            case 3:
            default:
                return "Unknown step"
        }
    }

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleCancel}
            onKeyDown={
                activeStep === steps.length - 1
                    ? confirmOnEnter(handleSubmit(onSubmitDataSource), isSubmitting || !isValid)
                    : confirmOnEnter(handleNext, activeStep === 1 && Boolean(error))
            }
            aria-describedby="alert-dialog-slide-description"
            fullWidth={true}
            maxWidth={"lg"}
        >
            <Box
                component="form"
                onSubmit={handleSubmit(onSubmitDataSource)}
                noValidate
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    m: 3,
                }}
            >
                <DialogTitle component={"h4"} sx={{ display: "flex", alignItems: "center", pb: 2 }}>
                    <ElectricalServicesIcon fontSize="large" />
                    {selectedAsset?._id ? selectedAsset.name : t("dataSource:titleNew")}
                </DialogTitle>
                <DialogContent>
                    <Grid>
                        <Stepper activeStep={activeStep}>
                            {steps.map((label, index) => {
                                const stepProps: { completed?: boolean } = {}
                                const labelProps: {
                                    optional?: React.ReactNode
                                } = {}
                                return (
                                    <Step key={label} {...stepProps} sx={{ py: 2 }}>
                                        <StepLabel {...labelProps}>{label}</StepLabel>
                                    </Step>
                                )
                            })}
                        </Stepper>
                        {getStepContent(activeStep)}
                    </Grid>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCancel} sx={{ px: 2 }} variant="text" color="inherit">
                        {t("disagree")}
                    </Button>
                    <Button
                        variant="text"
                        color="inherit"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ px: 2 }}
                    >
                        {t("dataSource:button.back")}
                    </Button>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            onClick={handleSubmit(onSubmitDataSource)}
                            disabled={isSubmitting || !isValid}
                            sx={{ px: 2 }}
                        >
                            {t("dataSource:button.finish")}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleNext}
                            disabled={activeStep === 1 && error} //disabled when JSON did not read correctly
                            sx={{ px: 2 }}
                        >
                            {t("dataSource:button.next")}
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    )
}

export default DataSourceDialog
